import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs,
  Grid,
  Divider,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import AdminChrome, {
  adminOutlineBtnSx,
  adminSolidBtnSx,
  adminStatPaperSx,
  adminSurfaceCardSx,
} from '../components/AdminChrome';
import { DOCUMENT, documentSubTabsSx } from '../constants/documentTheme';

const AdminPartnerApplications = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchApplications();
    
    // Check if there's an action in URL params
    const action = searchParams.get('action');
    const token = searchParams.get('token');
    
    if (action && token) {
      if (action === 'approve') {
        handleApproveFromEmail(token);
      } else if (action === 'reject') {
        handleRejectFromEmail(token);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/partners/applications');
      setApplications(response.data.applications);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveFromEmail = async (token) => {
    try {
      const response = await api.get(`/partners/applications/${token}`);
      const application = response.data.application;
      
      if (application.status !== 'pending') {
        setError(`This application has already been ${application.status}`);
        return;
      }
      
      if (window.confirm(`Approve partner application for ${application.company_name}?`)) {
        await handleApprove(application, token);
      }
    } catch (err) {
      console.error('Error fetching application:', err);
      setError('Failed to load application');
    }
  };

  const handleRejectFromEmail = async (token) => {
    try {
      const response = await api.get(`/partners/applications/${token}`);
      const application = response.data.application;
      
      if (application.status !== 'pending') {
        setError(`This application has already been ${application.status}`);
        return;
      }
      
      setSelectedApplication({ ...application, approval_token: token });
      setRejectDialogOpen(true);
    } catch (err) {
      console.error('Error fetching application:', err);
      setError('Failed to load application');
    }
  };

  const handleApprove = async (application, token = null) => {
    const approvalToken = token || application.approval_token;
    
    try {
      setActionLoading(true);
      await api.post(`/partners/applications/${approvalToken}/approve`);
      
      // Refresh applications
      await fetchApplications();
      
      alert(`Partner application for ${application.company_name} approved successfully!`);
      
      // Clear URL params
      navigate('/admin/partner-applications', { replace: true });
    } catch (err) {
      console.error('Error approving application:', err);
      alert(err.response?.data?.error || 'Failed to approve application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      setActionLoading(true);
      await api.post(`/partners/applications/${selectedApplication.approval_token}/reject`, {
        rejectionReason
      });
      
      // Refresh applications
      await fetchApplications();
      
      alert(`Partner application for ${selectedApplication.company_name} rejected.`);
      
      // Close dialog and clear
      setRejectDialogOpen(false);
      setSelectedApplication(null);
      setRejectionReason('');
      
      // Clear URL params
      navigate('/admin/partner-applications', { replace: true });
    } catch (err) {
      console.error('Error rejecting application:', err);
      alert(err.response?.data?.error || 'Failed to reject application');
    } finally {
      setActionLoading(false);
    }
  };

  const getBusinessTypeLabel = (type, typeOther) => {
    const types = {
      hotel: 'Hotel / Accommodation',
      restaurant: 'Restaurant / Food Service',
      tour_operator: 'Tour Operator',
      transport: 'Transportation Service',
      activity_provider: 'Activity Provider',
      other: typeOther || 'Other'
    };
    return types[type] || type;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const filterApplications = (status) => {
    if (status === 'all') return applications;
    return applications.filter(app => app.status === status);
  };

  const getFilteredApplications = () => {
    switch (activeTab) {
      case 0: return filterApplications('pending');
      case 1: return filterApplications('approved');
      case 2: return filterApplications('rejected');
      case 3: return filterApplications('all');
      default: return applications;
    }
  };

  const ApplicationCard = ({ application }) => (
    <Card sx={{ ...adminSurfaceCardSx, mb: 2 }}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon sx={{ mr: 1, color: DOCUMENT.oxblood }} />
              <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.2rem', color: DOCUMENT.ink }}>
                {application.company_name}
              </Typography>
              <Chip
                label={application.status}
                color={getStatusColor(application.status)}
                size="small"
                sx={{ ml: 2, borderRadius: 0 }}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <EmailIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  {application.contact_email}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <PhoneIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  {application.phone_number}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Contact:</strong> {application.contact_name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Business Type:</strong> {getBusinessTypeLabel(application.business_type, application.business_type_other)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Registration #:</strong> {application.business_registration_number}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Address:</strong> {application.company_address}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Applied:</strong> {new Date(application.created_at).toLocaleString()}
                </Typography>
              </Grid>
            </Grid>

            {application.rejection_reason && (
              <Box sx={{ mt: 2, p: 2, bgcolor: DOCUMENT.ivory, border: `1px solid ${DOCUMENT.ruleHard}` }}>
                <Typography variant="body2" sx={{ color: DOCUMENT.ink, fontWeight: 500 }}>
                  <strong>Rejection Reason:</strong> {application.rejection_reason}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            {application.status === 'pending' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  startIcon={<ApproveIcon />}
                  onClick={() => handleApprove(application)}
                  disabled={actionLoading}
                  fullWidth
                  sx={adminSolidBtnSx}
                >
                  Approve
                </Button>
                <Button
                  startIcon={<RejectIcon />}
                  onClick={() => {
                    setSelectedApplication(application);
                    setRejectDialogOpen(true);
                  }}
                  disabled={actionLoading}
                  fullWidth
                  sx={adminOutlineBtnSx}
                >
                  Reject
                </Button>
              </Box>
            )}
            {application.status === 'approved' && application.approved_at && (
              <Box>
                <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                  ✓ Approved on {new Date(application.approved_at).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            {application.status === 'rejected' && application.rejected_at && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                  ✗ Rejected on {new Date(application.rejected_at).toLocaleDateString()}
                </Typography>
                <Button
                  startIcon={<ApproveIcon />}
                  onClick={() => {
                    if (window.confirm(`Re-approve application for ${application.company_name}? A new password setup email will be sent.`)) {
                      handleApprove(application);
                    }
                  }}
                  disabled={actionLoading}
                  fullWidth
                  sx={adminSolidBtnSx}
                >
                  Approve Now
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <AdminChrome title={t('adminNav.applicationsTitle')} subtitle={t('adminNav.applicationsSubtitle')}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
          <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
        </Box>
      </AdminChrome>
    );
  }

  return (
    <AdminChrome
      title={t('adminNav.applicationsTitle')}
      subtitle={t('adminNav.applicationsSubtitle')}
      actions={(
        <Button startIcon={<RefreshIcon />} onClick={fetchApplications} disabled={loading} sx={adminOutlineBtnSx}>
          Refresh
        </Button>
      )}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 0 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { label: 'Pending', value: filterApplications('pending').length, color: DOCUMENT.accent },
          { label: 'Approved', value: filterApplications('approved').length, color: '#2e7d32' },
          { label: 'Rejected', value: filterApplications('rejected').length, color: DOCUMENT.oxblood },
          { label: 'Total', value: applications.length, color: DOCUMENT.ink },
        ].map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.label}>
            <Card sx={adminStatPaperSx}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                  {stat.label}
                </Typography>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 600, fontSize: '1.75rem', color: stat.color }}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{ ...documentSubTabsSx, mb: 3 }}
      >
        <Tab label={`Pending (${filterApplications('pending').length})`} disableRipple />
        <Tab label={`Approved (${filterApplications('approved').length})`} disableRipple />
        <Tab label={`Rejected (${filterApplications('rejected').length})`} disableRipple />
        <Tab label="All" disableRipple />
      </Tabs>

      {getFilteredApplications().length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          No applications found in this category.
        </Alert>
      ) : (
        getFilteredApplications().map((application) => (
          <ApplicationCard key={application.id} application={application} />
        ))
      )}

      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          if (!actionLoading) {
            setRejectDialogOpen(false);
            setSelectedApplication(null);
            setRejectionReason('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Reject Partner Application
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Company:</strong> {selectedApplication.company_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Contact:</strong> {selectedApplication.contact_name} ({selectedApplication.contact_email})
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
            disabled={actionLoading}
            helperText="This reason will be sent to the applicant via email"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setSelectedApplication(null);
              setRejectionReason('');
            }}
            disabled={actionLoading}
            sx={adminOutlineBtnSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            disabled={actionLoading || !rejectionReason.trim()}
            sx={adminSolidBtnSx}
          >
            {actionLoading ? 'Rejecting...' : 'Reject Application'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminChrome>
  );
};

export default AdminPartnerApplications;

